<template>
  <div ref="playerContainer" class="playerContainer">
    <video
      ref="videoPlayer"
      :poster="thumbnail"
      class="video-js vjs-big-play-centered"
    />
  </div>
</template>

<script>
// eslint-disable-next-line no-undef
require("!style-loader!css-loader!video.js/dist/video-js.css");
import videojs from "video.js";
import { overrideBaseTimeDisplayWithMs } from '@/extras/videojs/playback-time-in-ms.js';
// eslint-disable-next-line no-undef
require('videojs-offset');


// @group Media/Video
// Reactive video player based on VideoJS
export default {
  name: "VideoPlayer",
  components: {},
  props: {
    // VideoJS player options
    options: {
      type: Object,
      required: true
    },
    // Player sound volume in range [0:1]
    volume: {
      type: Number,
      default: 1,
      validator(val) {
        return val >= 0 && val <= 1;
      }
    },
    // Player muted flag
    muted: {
      type: Boolean,
      default: false
    },
    // Video source object
    src: {
      type: [Object, String],
      default: null
    },
    // Is video paused
    paused: {
      type: Boolean,
      default: true
    },
    // Thumbnail link
    thumbnail: {
      type: String,
      default: null
    },
    // Player width (in pixels)
    width: {
      type: Number,
      default: 800
    },
    // Player height (in pixels)
    height: {
      type: Number,
      default: 640
    },
    originalSize: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      player: null,
      reseted: true,
      poster: this.thumbnail
    };
  },
  computed: {
    srcLink() {
      return typeof this.src == "string" ? this.src : this.src?.src;
    }
  },
  watch: {
    // @vuese
    // Synchronize player volume when property changed
    volume(newVol, oldVal) {
      if (newVol != oldVal) {
        this.player.volume(newVol);
      }
    },
    // @vuese
    // Mute player when it calls externally
    muted() {
      this.player.muted(this.muted);
    },
    // @vuese
    // Set player source when property changed
    srcLink() {
      if (this.srcLink != null) {
        this.setSource(this.srcLink, this.thumbnail);
      } else {
        this.resetSource();
      }
    },
    // @vuese
    // Stop/start player when flag changed
    paused(paused) {
      if (paused) {
        this.pause();
      } else {
        this.play();
      }
    },
    // @vuese
    // Resize video
    width() {
      if (this.player != null) {
        this.getVideoElements().forEach(el => {
          el.style.width = `${this.width}px`;
        });
      }
    },
    // @vuese
    // Resize video
    height() {
      if (this.player != null) {
        this.getVideoElements().forEach(el => {
          el.style.height = `${this.height}px`;
        });
      }
    },
    // @vuese
    // Update player poster
    thumbnail() {
      if (this.player != null) {
        this.player.poster(this.thumbnail);
      }
    }
  },
  mounted() {
    if (!this.player) {
      overrideBaseTimeDisplayWithMs();
      this.player = videojs(this.$refs.videoPlayer, this.options, () => {
        this.$emit("playerReady");
      });
      window.playerplayer = this.player;
      if (this.originalSize) {
        this.player.fluid(false); //this.player.fill(false);
      }

      if (this.paused) {
        this.pause();
      } else {
        this.play();
      }

      this.setSource(this.src, this.thumbnail);

      // Add listeners
      this.player.on("volumechange", () => {
        /** volume changed by controls */
        if (this.player.volume != this.volume) {
          this.$emit("update:volume", this.player.volume);
        }

        /** player muted by controls */
        const actualMuted = this.player.muted();
        if (actualMuted != this.muted) {
          this.$emit("update:muted", actualMuted);
        }
      });
      this.player.on("durationchange", () =>
        this.$emit("durationchange", this.player.duration())
      );
      this.player.on("timeupdate", () =>
        this.$emit("timeupdate", this.player.currentTime())
      );
      // Player started
      this.player.on("play", () => this.$emit("played"));
      // Player stopped
      this.player.on("pause", () => this.$emit("paused"));
      // Player started loading
      this.player.on("waiting", () => this.$emit("loading"));
      // Player has done loading
      this.player.on("canplay", () => this.$emit("loaded"));
    }
  },
  beforeDestroy() {
    if (this.player) {
      this.player.dispose();
    }
  },
  methods: {
    durationVideoOffSet(start,end) {
      this.player.offset({
        start: start,
        end: end,
        restart_beginning: false //Should the video go to the beginning when it ends
      });
    },
    // @vuese
    // Set player source
    setSource(source, poster) {
      this.player.src(source);
      this.player.poster(poster);
    },
    // @vuese
    // Erase player source
    resetSource() {
      this.player.pause();
      this.player.src(null);
      this.player.poster(null);
    },
    // @vuese
    // Set player sound volume
    setVolume(val) {
      this.player.volume(val);
    },
    // @vuese
    // Set player current time
    setTime(time) {
      this.player.currentTime(time);
    },
    // @vuese
    // Start player
    play() {
      this.player.play().catch(e => {
        console.log(e);
        this.$emit("paused");
      });
    },
    // @vuese
    // Stop player
    pause() {
      this.player.pause();
    },
    // @vuese
    // Return video contaner to resize it
    getVideoElements() {
      const query =
        ".video-js.vjs-fluid, .video-js.vjs-16-9, .video-js.vjs-4-3, video.video-js, video.vjs-tech";
      return this.$refs.playerContainer.querySelectorAll(query);
    }
  }
};
</script>

<style>
.vjs-remaining-time {
  font-weight: bold !important;
}
.vjs-tech {
  /* width: 100% !important;
  height: 100% !important; */
}
</style>

<style scoped>
/* .playerContainer {
  width: fit-content;
  height: fit-content;
  margin: auto;
} */

.video-js.vjs-fluid,
.video-js.vjs-16-9,
.video-js.vjs-4-3,
video.video-js,
video.vjs-tech {
  position: relative !important;
  /** width: 100%; **/
  margin: auto !important;
  line-height: 0;
  object-fit: contain;
  /* left: 100px; */
  /* top: 20px; */
  /* bottom: 20px !important; */
  /* right: 300px; */
  /* width: 100%; */
  /* height: 100%; */
}
/* .video {
  width: 100%;
  height: 100%;
} */

.video-js.vjs-fluid:focus,
.video-js.vjs-16-9:focus,
.video-js.vjs-4-3:focus,
video.video-js:focus,
video.vjs-tech:focus {
  border: none !important;
  outline: none !important;
}
video:focus {
  border: none !important;
  outline: none !important;
}
/* Fix the control bar due to us resetting the line-height on the video-js */
.vjs-control-bar {
  line-height: 1;
}
.vjs-tech {
  object-fit: cover;
}
</style>
